import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Upload,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  History,
  Copy,
  X,
  Loader,
  Building2,
  Barcode,
} from "lucide-react";
import { sipGetStats, sipProcessOCR, sipGetWorkOrders, sipGetSupportedFunds, sipUploadFile } from "../api";
import "./SIPDashboard.css";

const SIPDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentWorkOrders, setRecentWorkOrders] = useState([]);
  const [supportedFunds, setSupportedFunds] = useState({});
  
  // Mode selection: 'upload' or 'barcode'
  const [processingMode, setProcessingMode] = useState("upload");
  
  // File Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uid, setUid] = useState("");
  
  // Barcode processing state
  const [barcodes, setBarcodes] = useState("");
  const [s3Bucket, setS3Bucket] = useState("sip-modernization");
  
  // Common state
  const [fundCode, setFundCode] = useState("");
  const [processing, setProcessing] = useState(false);
  const [processingResult, setProcessingResult] = useState(null);
  const [processingError, setProcessingError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsData, workOrdersData, fundsData] = await Promise.all([
        sipGetStats().catch(() => null),
        sipGetWorkOrders({ limit: 5 }).catch(() => ({ work_orders: [], total: 0 })),
        sipGetSupportedFunds().catch(() => ({ funds: {} }))
      ]);
      
      if (statsData) {
        setStats(statsData);
      }
      setRecentWorkOrders(workOrdersData?.work_orders || []);
      setSupportedFunds(fundsData?.funds || {});
      
      // Update stats from work orders if stats endpoint not available
      if (!statsData && workOrdersData?.work_orders) {
        const total = workOrdersData.total || workOrdersData.work_orders.length;
        const success = workOrdersData.work_orders.filter(wo => wo.success === true).length;
        const failed = workOrdersData.work_orders.filter(wo => wo.success === false).length;
        setStats({
          total_work_orders: total,
          by_status: { completed: success, failed: failed, processing: 0 }
        });
      }
    } catch (err) {
      console.error("Failed to fetch SIP data:", err);
    } finally {
      setLoading(false);
    }
  };

  // File upload handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setProcessingResult(null);
      setProcessingError(null);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setProcessingResult(null);
      setProcessingError(null);
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Process handler - supports both file upload and barcode modes
  const handleProcess = async () => {
    if (!fundCode) return;

    setProcessing(true);
    setProcessingError(null);
    setProcessingResult(null);

    try {
      let result;
      
      if (processingMode === "upload") {
        // File upload mode
        if (!selectedFile) {
          setProcessingError("Please select a file to upload");
          setProcessing(false);
          return;
        }
        result = await sipUploadFile(selectedFile, fundCode, uid || undefined);
      } else {
        // Barcode processing mode
        if (!barcodes.trim()) {
          setProcessingError("Please enter at least one barcode");
          setProcessing(false);
          return;
        }
        const barcodeList = barcodes.split(',').map(b => b.trim()).filter(b => b);
        result = await sipProcessOCR({
          fund: fundCode,
          barcode: barcodeList,
          dump_s3_bucket: s3Bucket || 'sip-modernization'
        });
      }
      
      setProcessingResult(result);
      // Refresh data after processing
      fetchData();
    } catch (err) {
      console.error("Processing failed:", err);
      setProcessingError(err.message || "Processing failed");
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const clearResult = () => {
    setProcessingResult(null);
    setProcessingError(null);
    setFundCode("");
    setBarcodes("");
    setSelectedFile(null);
    setUid("");
  };

  const getStatusBadge = (wo) => {
    if (wo.success === true) {
      return <span className="sip-status-badge success">Success</span>;
    } else if (wo.success === false) {
      return <span className="sip-status-badge failed">Failed</span>;
    }
    return <span className="sip-status-badge pending">Pending</span>;
  };

  return (
    <div className="sip-dashboard">
      {/* Welcome Banner */}
      <div className="sip-welcome-banner">
        <div className="sip-welcome-content">
          <h1>SIP OCR Pipeline</h1>
          <p>Process SIP forms using AI-powered OCR with support for 20+ AMCs and multiple transaction types</p>
        </div>
        <div className="sip-welcome-actions">
          <button className="sip-welcome-btn primary" onClick={() => navigate("/sip/history")}>
            <History size={20} />
            View All Work Orders
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="sip-stats-overview">
        <div className="sip-stat-card total">
          <div className="sip-stat-icon">
            <FileText size={24} />
          </div>
          <div className="sip-stat-info">
            <span className="sip-stat-value">{loading ? "-" : stats?.total_work_orders || 0}</span>
            <span className="sip-stat-label">Total Processed</span>
          </div>
        </div>

        <div className="sip-stat-card completed">
          <div className="sip-stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="sip-stat-info">
            <span className="sip-stat-value">{loading ? "-" : stats?.by_status?.completed || 0}</span>
            <span className="sip-stat-label">Successful</span>
          </div>
        </div>

        <div className="sip-stat-card processing">
          <div className="sip-stat-icon">
            <Clock size={24} />
          </div>
          <div className="sip-stat-info">
            <span className="sip-stat-value">{loading ? "-" : stats?.by_status?.processing || 0}</span>
            <span className="sip-stat-label">Processing</span>
          </div>
        </div>

        <div className="sip-stat-card failed">
          <div className="sip-stat-icon">
            <AlertCircle size={24} />
          </div>
          <div className="sip-stat-info">
            <span className="sip-stat-value">{loading ? "-" : stats?.by_status?.failed || 0}</span>
            <span className="sip-stat-label">Failed</span>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="sip-main-grid">
        {/* Left: Process OCR Form */}
        <div className="sip-card">
          <div className="sip-card-header">
            <Upload size={20} />
            <span>Process SIP OCR</span>
          </div>
          <div className="sip-card-body">
            {/* Mode Tabs */}
            <div className="sip-mode-tabs">
              <button
                className={`sip-mode-tab ${processingMode === 'upload' ? 'active' : ''}`}
                onClick={() => setProcessingMode('upload')}
              >
                <Upload size={16} />
                File Upload
              </button>
              <button
                className={`sip-mode-tab ${processingMode === 'barcode' ? 'active' : ''}`}
                onClick={() => setProcessingMode('barcode')}
              >
                <Barcode size={16} />
                Barcode Lookup
              </button>
            </div>

            {/* Fund Code Selection (common to both modes) */}
            <div className="sip-form-group">
              <label htmlFor="sip-fund">Fund Code *</label>
              <select
                id="sip-fund"
                value={fundCode}
                onChange={(e) => setFundCode(e.target.value)}
                className="sip-select"
              >
                <option value="">Select Fund...</option>
                {Object.entries(supportedFunds).map(([code, name]) => (
                  <option key={code} value={code}>{code} - {name}</option>
                ))}
              </select>
            </div>

            {/* File Upload Mode */}
            {processingMode === 'upload' && (
              <>
                {/* File Upload Zone */}
                <div 
                  className={`sip-upload-zone ${selectedFile ? 'has-file' : ''}`}
                  onClick={() => document.getElementById('sip-file-input').click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                >
                  {selectedFile ? (
                    <>
                      <FileText size={32} className="sip-file-icon" />
                      <p className="sip-file-name">{selectedFile.name}</p>
                      <p className="sip-file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </>
                  ) : (
                    <>
                      <Upload size={32} />
                      <p>Click or drag file here to upload</p>
                      <p className="sip-upload-hint">PDF, TIFF, JPG, PNG (max 50MB)</p>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  id="sip-file-input"
                  accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                {/* UID Input */}
                <div className="sip-form-group">
                  <label htmlFor="sip-uid">UID (optional tracking ID)</label>
                  <input
                    type="text"
                    id="sip-uid"
                    value={uid}
                    onChange={(e) => setUid(e.target.value)}
                    placeholder="Optional unique identifier for tracking"
                    className="sip-input"
                  />
                </div>
              </>
            )}

            {/* Barcode Mode */}
            {processingMode === 'barcode' && (
              <>
                {/* Barcodes Input */}
                <div className="sip-form-group">
                  <label htmlFor="sip-barcodes">Barcode(s) *</label>
                  <input
                    type="text"
                    id="sip-barcodes"
                    value={barcodes}
                    onChange={(e) => setBarcodes(e.target.value)}
                    placeholder="Enter barcode(s), comma-separated for multiple"
                    className="sip-input"
                  />
                  <span className="sip-input-hint">Enter one or more barcodes separated by commas</span>
                </div>

                {/* S3 Bucket Input */}
                <div className="sip-form-group">
                  <label htmlFor="sip-bucket">S3 Bucket (optional)</label>
                  <input
                    type="text"
                    id="sip-bucket"
                    value={s3Bucket}
                    onChange={(e) => setS3Bucket(e.target.value)}
                    placeholder="sip-modernization"
                    className="sip-input"
                  />
                </div>
              </>
            )}

            {/* Process Button */}
            <button
              className="sip-process-btn"
              onClick={handleProcess}
              disabled={!fundCode || (processingMode === 'upload' ? !selectedFile : !barcodes.trim()) || processing}
            >
              {processing ? (
                <>
                  <Loader size={18} className="sip-spinner" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  {processingMode === 'upload' ? 'Upload & Extract' : 'Process OCR'}
                </>
              )}
            </button>

            {/* Error Display */}
            {processingError && (
              <div className="sip-error-message">
                <AlertCircle size={16} />
                <span>{processingError}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right: Results */}
        <div className="sip-card">
          <div className="sip-card-header">
            <TrendingUp size={20} />
            <span>Processing Results</span>
            {processingResult && (
              <button className="sip-clear-btn" onClick={clearResult}>
                <X size={16} />
              </button>
            )}
          </div>
          <div className="sip-card-body">
            {processingResult ? (
              <div className="sip-result-section">
                {/* Result Meta */}
                <div className="sip-result-meta">
                  <div className="sip-meta-item">
                    <span>Status:</span>
                    <span className={`sip-status-badge ${processingResult.success || processingResult.body?.success ? 'success' : 'failed'}`}>
                      {processingResult.success || processingResult.body?.success ? 'Success' : 'Failed'}
                    </span>
                  </div>
                  {(processingResult.execution_id || processingResult.body?.execution_id) && (
                    <div className="sip-meta-item">
                      <span>Execution ID:</span>
                      <strong>{processingResult.execution_id || processingResult.body?.execution_id}</strong>
                    </div>
                  )}
                  {(processingResult.uid || processingResult.body?.uid) && (
                    <div className="sip-meta-item">
                      <span>UID:</span>
                      <strong>{processingResult.uid || processingResult.body?.uid}</strong>
                    </div>
                  )}
                  {(processingResult.barcode || processingResult.body?.barcode) && (
                    <div className="sip-meta-item">
                      <span>Barcode:</span>
                      <strong>{processingResult.barcode || processingResult.body?.barcode}</strong>
                    </div>
                  )}
                  {(processingResult.processing_time || processingResult.body?.processing_time) && (
                    <div className="sip-meta-item">
                      <span>Processing Time:</span>
                      <strong>{processingResult.processing_time || processingResult.body?.processing_time}</strong>
                    </div>
                  )}
                </div>

                {/* Extracted Data Section */}
                {(processingResult.extracted_data || processingResult.body?.extracted_data) && 
                  Object.keys(processingResult.extracted_data || processingResult.body?.extracted_data).length > 0 && (
                  <div className="sip-extracted-data-section">
                    <div className="sip-result-json-header">
                      <strong>Extracted Data (SO_*/OO_* Fields)</strong>
                      <button
                        className="sip-copy-btn"
                        onClick={() => copyToClipboard(JSON.stringify(processingResult.extracted_data || processingResult.body?.extracted_data, null, 2))}
                      >
                        <Copy size={14} />
                        Copy
                      </button>
                    </div>
                    <div className="sip-extracted-data-grid">
                      {Object.entries(processingResult.extracted_data || processingResult.body?.extracted_data)
                        .filter(([key]) => key.startsWith('SO_') || key.startsWith('OO_'))
                        .map(([key, value]) => (
                          <div key={key} className="sip-data-item">
                            <span className="sip-data-label">{key.replace(/^(SO_|OO_)/, '')}</span>
                            <span className="sip-data-value">{value || '-'}</span>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}

                {/* Full Response JSON */}
                <div className="sip-result-json-header">
                  <strong>Full Response</strong>
                  <button
                    className="sip-copy-btn"
                    onClick={() => copyToClipboard(JSON.stringify(processingResult, null, 2))}
                  >
                    <Copy size={14} />
                    Copy JSON
                  </button>
                </div>
                <pre className="sip-result-json">
                  {JSON.stringify(processingResult, null, 2)}
                </pre>
              </div>
            ) : (
              <div className="sip-no-results">
                <FileText size={48} />
                <p>Select a fund and enter barcodes to process SIP OCR</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Supported AMCs Section */}
      <div className="sip-amcs-section">
        <h2 className="sip-section-title">
          <Building2 size={20} />
          Supported AMCs ({Object.keys(supportedFunds).length})
        </h2>
        <div className="sip-amcs-grid">
          {Object.entries(supportedFunds).map(([code, name]) => (
            <div key={code} className="sip-amc-item">
              <span className="sip-amc-code">{code}</span>
              <span className="sip-amc-name">{name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Work Orders */}
      <div className="sip-recent-section">
        <h2 className="sip-section-title">
          <History size={20} />
          Recent Processing
        </h2>
        
        {recentWorkOrders.length > 0 ? (
          <div className="sip-recent-list">
            {recentWorkOrders.map((wo) => (
              <div
                key={wo._id || wo.execution_id}
                className="sip-recent-item"
                onClick={() => navigate(`/sip/work-order/${wo.execution_id || wo._id}`)}
              >
                <div className="sip-recent-icon">
                  <FileText size={20} />
                </div>
                <div className="sip-recent-info">
                  <div className="sip-recent-title">
                    {wo.barcode || wo.execution_id || 'Work Order'}
                  </div>
                  <div className="sip-recent-meta">
                    <span>Fund: {wo.fund || '-'}</span>
                    <span>{wo.transaction_type || wo.trtype || '-'}</span>
                    <span>{wo.created_at && new Date(wo.created_at).toLocaleString()}</span>
                  </div>
                </div>
                {getStatusBadge(wo)}
              </div>
            ))}
          </div>
        ) : (
          <div className="sip-empty-recent">
            <p>No recent processing. Start by entering a barcode above.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SIPDashboard;
